import React from "react"
import { Control, FieldErrors, FieldValues, UseFormSetValue, UseFormWatch } from "react-hook-form"

import { ProductImageDto } from "@/api/api-client"
import { FormSectionHeader } from "@/components/ui/form-section-header"
import { useLanguage } from "@/hooks/useLanguage"

import { ProductAssetFields } from "./product-fields/ProductAssetFields"
import { ProductBasicFields } from "./product-fields/ProductBasicFields"
import { ProductInventoryFields } from "./product-fields/ProductInventoryFields"
import { ProductPricingFields } from "./product-fields/ProductPricingFields"
import { useProductFormOptions } from "./product-fields/useProductFormOptions"

interface ProductFieldsProps {
  register: unknown
  errors: FieldErrors<FieldValues>
  setValue: UseFormSetValue<FieldValues>
  watch: UseFormWatch<FieldValues>
  control: Control<FieldValues>
  readOnlyName?: boolean
  showFiles?: boolean
  existingImages?: ProductImageDto[]
  existingTechSpec?: string
  onDeleteImage?: (id: number) => void
  translations?: unknown
  language?: unknown
}

export const ProductFields: React.FC<ProductFieldsProps> = ({
  errors,
  setValue,
  watch,
  control,
  readOnlyName = false,
  showFiles = true,
  existingImages = [],
  existingTechSpec,
  onDeleteImage,
}) => {
  const { t, language } = useLanguage()
  const text = t.productForm
  const imageFiles = (watch("imageFiles") as File[] | undefined) || []
  const primaryImageIndex = Number(watch("primaryImageIndex") || 0)
  const price = Number(watch("price") || 0)
  const isTaxable = Boolean(watch("isTaxable"))
  const weightUnit = String(watch("weightUnit") || "kg")
  const { categoryOptions, providerOptions, warehouseOptions } = useProductFormOptions(language)

  return (
    <div className="space-y-16">
      <ProductBasicFields
        control={control}
        labels={text}
        readOnlyName={readOnlyName}
        categoryOptions={categoryOptions}
        providerOptions={providerOptions}
      />

      <section>
        <FormSectionHeader number="02" title={text.section2Title} />
        <div className="space-y-12">
          <ProductPricingFields
            control={control}
            labels={text}
            price={price}
            isTaxable={isTaxable}
          />

          {!readOnlyName && (
            <ProductInventoryFields
              control={control}
              labels={text}
              warehouseOptions={warehouseOptions}
              weightUnit={weightUnit}
            />
          )}
        </div>
      </section>

      <ProductAssetFields
        control={control}
        labels={text}
        errors={errors}
        setValue={setValue}
        imageFiles={imageFiles}
        primaryImageIndex={primaryImageIndex}
        showFiles={showFiles}
        existingImages={existingImages}
        existingTechSpec={existingTechSpec}
        onDeleteImage={onDeleteImage}
      />
    </div>
  )
}
